import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchData } from '../../../reusable'
import moment from 'moment'

// ─── Types ───
interface SelectOption {
  value: string | number
  label: string
}

interface DemoFilters {
  category: SelectOption | null
  dateRange: Date[]
  country: SelectOption | null
  region: SelectOption | null
  gender: SelectOption | null
  ageRange: SelectOption | null
  device: SelectOption | null
}

export interface DemoSummary {
  totalGuests: number
  totalBookings: number
  topCountry: { country: string; guests: number } | string
  repeatGuests: number
}

export interface GuestBooking {
  userId: number
  guestName: string
  email: string
  gender: string
  age: number
  country: string
  region: string
  device: string
  totalBookings: number
  totalAmount: number
  lastBookingDate: string
}

interface FilterParams {
  categoryId?: string | number
  country?: string | number
  region?: string | number
  regionId?: string | number
  gender?: string
  device?: string
  ageFrom?: number
  ageTo?: number
  fromDate?: string
  toDate?: string
  limit?: number
  offset?: number
}

const DEFAULT_SUMMARY: DemoSummary = {
  totalGuests: 0,
  totalBookings: 0,
  topCountry: '-',
  repeatGuests: 0,
}

// ─── Age Range Mapping ───
const AGE_RANGE_MAP: Record<string, { ageFrom: number; ageTo: number }> = {
  '18-25': { ageFrom: 18, ageTo: 25 },
  '26-35': { ageFrom: 26, ageTo: 35 },
  '36-45': { ageFrom: 36, ageTo: 45 },
  '46-55': { ageFrom: 46, ageTo: 55 },
  '55+': { ageFrom: 55, ageTo: 120 },
}

// ─── Helpers ───
const buildParams = (filters: DemoFilters): FilterParams => {
  const params: FilterParams = {}
  if (filters.category?.value) params.categoryId = filters.category.value
  if (filters.country?.value) params.country = filters.country.value
  if (filters.region?.value) params.region = filters.region.value
  if (filters.gender?.value) params.gender = String(filters.gender.value)
  if (filters.device?.value) params.device = String(filters.device.value)
  if (filters.ageRange?.value) {
    const range = AGE_RANGE_MAP[String(filters.ageRange.value)]
    if (range) {
      params.ageFrom = range.ageFrom
      params.ageTo = range.ageTo
    }
  }
  if (filters.dateRange?.length === 2) {
    params.fromDate = moment(filters.dateRange[0]).format('DD-MM-YYYY')
    params.toDate = moment(filters.dateRange[1]).format('DD-MM-YYYY')
  }
  return params
}

const safeApiCall = async (endpoint: string, params: Record<string, any>): Promise<any> => {
  try {
    const res = await fetchData(endpoint, params)
    if (res?.status == '200' && res?.data) return res.data
    return null
  } catch {
    return null
  }
}

// ─── Main Hook ───
export const useDemographics = (filters: DemoFilters) => {
  const [summary, setSummary] = useState<DemoSummary>(DEFAULT_SUMMARY)
  const [bookings, setBookings] = useState<GuestBooking[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  const fetchAll = useCallback(async () => {
    setIsLoading(true)
    const params: FilterParams = { ...buildParams(filters), limit, offset }

    const [summaryData, bookingData] = await Promise.all([
      safeApiCall('admin/demographicssummary', params),
      safeApiCall('admin/guestbookings', params),
    ])

    if (!isMounted.current) return

    setSummary(summaryData ?? DEFAULT_SUMMARY)
    setBookings(Array.isArray(bookingData?.list) ? bookingData.list : [])
    setTotalRecords(Number(bookingData?.totalRecords) || 0)
    setIsLoading(false)
  }, [filters, limit, offset])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const handlePageChange = useCallback((page: number) => {
    const totalPages = Math.max(Math.ceil(totalRecords / limit), 1)
    if (page === 1) setOffset(0)
    else if (page === totalPages) setOffset((totalPages - 1) * limit)
    else setOffset((page - 1) * limit)
  }, [totalRecords, limit])

  const handleRowsPerPageChange = useCallback((newLimit: number) => {
    setLimit(newLimit)
    setOffset(0)
  }, [])

  return { summary, bookings, totalRecords, isLoading, limit, handlePageChange, handleRowsPerPageChange }
}

// ─── Country List Hook ───
export const useCountryList = (): SelectOption[] => {
  const [list, setList] = useState<SelectOption[]>([])
  useEffect(() => {
    const fetch = async () => {
      const data = await safeApiCall('vendor/public/countryList', {})
      if (Array.isArray(data)) {
        setList(data.map((c: any) => ({
          value: c.countryId ?? c.id ?? '',
          label: c.nicename || c.en_name || c.name || '-',
        })))
      }
    }
    fetch()
  }, [])
  return list
}

// ─── Region List Hook ───
export const useRegionList = (): SelectOption[] => {
  const [list, setList] = useState<SelectOption[]>([])
  useEffect(() => {
    const fetch = async () => {
      const data = await safeApiCall('vendor/public/regionList', {})
      if (Array.isArray(data)) {
        setList(data.map((r: any) => ({
          value: r.regionId ?? r.id ?? '',
          label: r.en_name || r.name || '-',
        })))
      }
    }
    fetch()
  }, [])
  return list
}
